import * as React from 'react';
import Select from 'react-select';
import {
    ConversationalTarget as IConversationalTarget,
    TARGET_ACTIONS
} from '@nexxt/common/types';
import {
    TARGET_ACTIONS_ITEMS,
    TARGET_DETECTION_TYPES
} from '../../../../../constants/project';
import {
    UilTrashAlt,
    UilAngleUp,
    UilAngleDown,
    UilDraggabledots
} from '@iconscout/react-unicons';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';

interface ConversationalTargetProps {
    target: IConversationalTarget;
    order: number;
    enablePriority: boolean;
    disabled: boolean;
    onTargetChange: (keyName: string, value: any) => void;
    onTargetDelete: () => void;
    totalTargets: number;
    isSmartProbeEnabled: boolean;
}

const ConversationalTarget = ({
    target,
    order,
    disabled,
    enablePriority,
    onTargetChange,
    onTargetDelete,
    totalTargets,
    isSmartProbeEnabled
}: ConversationalTargetProps): JSX.Element => {
    const [expanded, setExpanded] = React.useState<boolean>(true);
    React.useEffect(() => {
        /** Disable target action when probing is not enabled. */
        if (!isSmartProbeEnabled && target?.action !== TARGET_ACTIONS.NONE)
            onTargetChange('action', TARGET_ACTIONS.NONE);
    }, [isSmartProbeEnabled, target]);
    const selectedAction = TARGET_ACTIONS_ITEMS.find(
        (item) => item.value === target.action
    );
    const selectedDetectionType =
        TARGET_DETECTION_TYPES.find(
            (item) => item.value === target.detectionExamplesSource
        ) ?? TARGET_DETECTION_TYPES[0];
    const maxNumExamples = 3;
    const targetPriorityItems = Array.from(
        { length: totalTargets + 1 },
        (v, i) => ({
            label: i ? i : 'None',
            value: i ? i : null
        })
    );
    const selectedPriority =
        targetPriorityItems.find((item) => item.value === target.priority) ??
        targetPriorityItems[0];

    const handleExamples = (index: number, keyName: string, value: string) => {
        const examples = target[keyName];
        const currentExample = examples?.[index];

        if (currentExample) {
            onTargetChange(keyName, [
                ...examples.slice(0, index),
                value,
                ...examples.slice(index + 1)
            ]);
        } else {
            examples.push(value);
            onTargetChange(keyName, examples);
        }
    };
    return (
        <div className="smartProbe--target--box">
            <div className="smartProbe--target--title">
                <span className="smartProbe--target--title--actions">
                    {/* {enablePriority ? (
                        <div className="smartProbe--target--title--drag-handle">
                            <DragHandle />
                        </div>
                    ) : null} */}
                    Conversational Target:{' '}
                    {target.label || `Target Label ${order}`}
                </span>
                <span className="smartProbe--target--title--actions">
                    <span onClick={onTargetDelete}>
                        <UilTrashAlt size={20} />
                    </span>
                    <span onClick={() => setExpanded((toggle) => !toggle)}>
                        {expanded ? (
                            <UilAngleUp size={20} />
                        ) : (
                            <UilAngleDown size={20} />
                        )}
                    </span>
                </span>
            </div>
            {expanded ? (
                <div className="smartProbe--target--body px-4">
                    <div className="smartProbe--target--row gap-4">
                        <div className="form-group flex-1">
                            <label className="form-group__label form-group-header form-group__label--block">
                                Target label
                            </label>
                            <input
                                className="form-group__control"
                                disabled={disabled}
                                placeholder="Target label"
                                value={target.label}
                                onChange={(e) =>
                                    onTargetChange('label', e.target.value)
                                }
                            />
                        </div>
                        <div className="flex gap-4">
                            {enablePriority ? (
                                <div className="form-group no-margin w-24">
                                    <label className="form-group__label form-group-header form-group__label--block">
                                        Priority
                                    </label>
                                    <Select
                                        options={targetPriorityItems}
                                        placeholder="#"
                                        isDisabled={disabled}
                                        value={selectedPriority}
                                        onChange={(select) =>
                                            onTargetChange(
                                                'priority',
                                                select.value
                                            )
                                        }
                                    />
                                </div>
                            ) : null}
                            <div className="form-group no-margin w-44">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Action
                                </label>
                                <Select
                                    options={TARGET_ACTIONS_ITEMS}
                                    placeholder="Select Target Action"
                                    isDisabled={
                                        disabled || !isSmartProbeEnabled
                                    }
                                    value={selectedAction}
                                    onChange={(select) =>
                                        onTargetChange('action', select.value)
                                    }
                                />
                            </div>

                            <div className="form-group no-margin w-44">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Detection Type
                                </label>
                                <Select
                                    options={TARGET_DETECTION_TYPES}
                                    placeholder="Select Detection Type"
                                    isDisabled={disabled}
                                    value={selectedDetectionType}
                                    onChange={(select) =>
                                        onTargetChange(
                                            'detectionExamplesSource',
                                            select.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    {target.detectionExamplesSource && (
                        <div className="smartProbe--target--row gap-4">
                            <div className="form-group">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Examples (OPTIONAL)
                                </label>
                                <span>
                                    Regional variations or similar concepts, if
                                    applicable
                                </span>

                                <div className="smartProbe--target--examples">
                                    {Array.from(
                                        Array(maxNumExamples).keys()
                                    ).map((index) => {
                                        return (
                                            <input
                                                key={`example-${index}`}
                                                className="form-group__control"
                                                disabled={disabled}
                                                placeholder={`Target Examples ${
                                                    index + 1
                                                }`}
                                                onChange={(e) =>
                                                    handleExamples(
                                                        index,
                                                        'detection_examples',
                                                        e.target.value
                                                    )
                                                }
                                                value={
                                                    target.detection_examples?.[
                                                        index
                                                    ]
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Counter Examples (OPTIONAL)
                                </label>
                                <span>
                                    Similar phrases which you do NOT want to
                                    match, if applicable
                                </span>
                                <div className="smartProbe--target--examples">
                                    {Array.from(
                                        Array(maxNumExamples).keys()
                                    ).map((index) => {
                                        return (
                                            <input
                                                key={`counterexample-${index}`}
                                                className="form-group__control"
                                                disabled={disabled}
                                                placeholder={`Target Counter Examples ${
                                                    index + 1
                                                }`}
                                                onChange={(e) =>
                                                    handleExamples(
                                                        index,
                                                        'detection_counterexamples',
                                                        e.target.value
                                                    )
                                                }
                                                value={
                                                    target
                                                        .detection_counterexamples?.[
                                                        index
                                                    ]
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

const DragHandle = SortableHandle(() => <UilDraggabledots size={20} />);

export default SortableElement(ConversationalTarget);
